import { useQuery } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { api, req } from '@/lib/api/client';
import type { Schemas } from '@/lib/api/types';
import { Button } from '@/components/Button';
import { cx } from '@/lib/format';
import { safeNext } from './safeNext';

export type AuthProviders = Schemas['AuthProviders'];
export type AuthProvider = Schemas['AuthProvider'];

/** `GET /auth/providers` — public; cached for the session. Errors resolve to "nothing configured". */
export function useAuthProviders() {
  return useQuery<AuthProviders>({
    queryKey: ['auth', 'providers'],
    queryFn: () => req(api.GET('/auth/providers')),
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** Resolve the provider's absolute `start_url` and append the post-login path (relative paths only). */
export function startUrl(p: AuthProvider, next: string): string {
  const u = new URL(p.start_url, location.origin);
  const n = safeNext(next);
  if (n !== '/') u.searchParams.set('next', n);
  return u.toString();
}

/** Human message for `/login?error=<code>` redirects from the OIDC callback. */
export function oidcErrorMessage(code: string): string {
  switch (code) {
    case 'oidc_state':
    case 'oidc_nonce':
      return 'The sign-in attempt expired or was tampered with. Please try again.';
    case 'oidc_denied':
    case 'access_denied':
      return 'Sign-in was cancelled at the identity provider.';
    case 'oidc_email_unverified':
      return 'Your identity provider has not verified this email address.';
    case 'oidc_no_email':
      return 'Your identity provider did not share an email address, which Lumen needs to find your account.';
    case 'email_not_verified':
      return 'Verify your email address first (check your inbox for the verification link), then sign in again.';
    case 'signup_disabled':
      return 'Sign-up is disabled on this deployment; ask an administrator for an invitation.';
    case 'oidc_unavailable':
      return 'Single sign-on is not configured on this deployment.';
    case 'oidc_failed':
      return 'The identity provider rejected the sign-in. Please try again or use another method.';
    default:
      return `Single sign-on failed (${code}).`;
  }
}

/**
 * OAuth / OpenID Connect buttons for the login and sign-up forms. Renders nothing while loading
 * and when the deployment has no providers, so password-only installs see no extra UI.
 */
export function OAuthButtons({ next, mode, className }: { next: string; mode: 'login' | 'signup'; className?: string }) {
  const q = useAuthProviders();
  const providers = q.data?.providers ?? [];
  if (providers.length === 0) return null;
  return (
    <div className={cx('space-y-2', className)} data-oauth-buttons>
      <div className="flex items-center gap-3 text-xs text-fg-subtle" aria-hidden="true">
        <span className="h-px flex-1 bg-line" />
        or
        <span className="h-px flex-1 bg-line" />
      </div>
      {providers.map((p) => (
        <Button key={p.id} type="button" className="w-full" onClick={() => location.assign(startUrl(p, next))} data-oauth-provider={p.id}>
          <KeyRound size={14} /> {mode === 'signup' ? 'Sign up' : 'Continue'} with {p.name}
        </Button>
      ))}
    </div>
  );
}
